<template>
  <section class="section">
    <div class="container">
      <div class="page-head">
        <div>
          <h1 class="page-title">Censo de familias</h1>
          <p class="page-sub">{{ subtitulo }}</p>
        </div>
        <div class="page-actions">
          <b-button type="is-primary" icon-left="home-plus" @click="abrirNuevaFamilia">Nueva familia</b-button>
        </div>
      </div>

      <div class="stat-cards">
        <div class="stat-card tint-violet">
          <div class="stat-label">Familias</div>
          <div class="stat-value">{{ resumen.total_familias || 0 }}</div>
          <div class="stat-caption">{{ resumen.familias_activas || 0 }} activas</div>
        </div>
        <div class="stat-card tint-blue">
          <div class="stat-label">Personas censadas</div>
          <div class="stat-value">{{ resumen.miembros_en_familia || 0 }}</div>
          <div class="stat-caption">De {{ resumen.total_miembros || 0 }} miembros registrados</div>
        </div>
        <div class="stat-card tint-amber">
          <div class="stat-label">Sin familia</div>
          <div class="stat-value">{{ resumen.miembros_sin_familia || 0 }}</div>
          <div class="stat-caption">Miembros por asignar</div>
        </div>
        <div class="stat-card tint-green">
          <div class="stat-label">Cobertura</div>
          <div class="stat-value">{{ cobertura }}%</div>
          <div class="stat-caption">Miembros dentro de una familia</div>
        </div>
      </div>

      <div class="toolbar-panel">
        <p class="toolbar-label">Filtros</p>
        <b-field grouped>
          <b-input placeholder="Buscar familia por nombre o apellido" v-model="filtro" icon="magnify" expanded></b-input>
          <b-select v-model="filtroEstado">
            <option value="todas">Todas</option>
            <option value="activas">Solo activas</option>
            <option value="inactivas">Solo inactivas</option>
          </b-select>
          <b-select v-if="vistaGlobalSuper" v-model="filtroGrupo" placeholder="Todos los grupos">
            <option :value="null">Todos los grupos</option>
            <option v-for="g in gruposOrdenados" :value="g.id" :key="g.id">{{ g.nombre }}</option>
          </b-select>
        </b-field>
      </div>

      <div v-if="cargando" class="censo-loading">Cargando censo…</div>

      <div v-else>
        <div v-if="familiasVisibles.length === 0" class="empty-card">
          <b-icon icon="home-group" size="is-large"></b-icon>
          <p>No hay familias registradas{{ filtro || filtroEstado !== 'todas' ? ' con estos filtros' : '' }}.</p>
          <b-button type="is-primary is-light" icon-left="plus" @click="abrirNuevaFamilia">Crear la primera familia</b-button>
        </div>

        <div v-for="fam in familiasVisibles" :key="fam.id" class="family-card" :class="{ 'is-inactive': !fam.activa }">
          <div class="family-head">
            <div class="family-title">
              <div class="family-icon"><b-icon icon="home-account" size="is-small"></b-icon></div>
              <div>
                <h3>{{ nombreFamilia(fam) }}</h3>
                <p class="family-meta">
                  {{ fam.total_miembros }} {{ fam.total_miembros == 1 ? 'miembro' : 'miembros' }}
                  <span v-if="fam.iglesia"> · {{ fam.iglesia }}</span>
                  <span v-if="vistaGlobalSuper"> · {{ nombreGrupo(fam.grupoId) }}</span>
                </p>
              </div>
            </div>
            <div class="family-badges">
              <span class="tag" :class="fam.activa ? 'is-success' : 'is-danger'">{{ fam.activa ? 'Activa' : 'Inactiva' }}</span>
              <span v-if="vistaGlobalSuper" class="tag" :style="estiloGrupo(fam.grupoId)">{{ nombreGrupo(fam.grupoId) }}</span>
            </div>
          </div>

          <div v-if="fam.direccion || fam.ciudad" class="family-address">
            <b-icon icon="map-marker-outline" size="is-small"></b-icon>
            <span>
              {{ fam.direccion || '' }}<template v-if="fam.ciudad">, {{ fam.ciudad }}</template><template v-if="fam.estado">, {{ fam.estado }}</template><template v-if="fam.codigo_postal"> C.P. {{ fam.codigo_postal }}</template>
            </span>
          </div>

          <div class="family-members">
            <table v-if="fam.miembros && fam.miembros.length" class="table members-table">
              <thead>
                <tr>
                  <th>Miembro</th>
                  <th>Categoría</th>
                  <th>Edad</th>
                  <th>Estado</th>
                  <th>Asistencia</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="m in miembrosOrdenados(fam)" :key="m.id">
                  <td><span v-if="m.rol_familiar" class="tag rol-tag" :class="claseRol(m.rol_familiar)">{{ etiquetaRol(m.rol_familiar) }}</span> <strong>{{ nombreCompleto(m) }}</strong></td>
                  <td><span v-if="m.categoria" class="tag is-info">{{ m.categoria }}</span><span v-else class="muted">—</span></td>
                  <td>{{ edadDe(m) }}</td>
                  <td><span class="tag" :class="claseEstado(m.estado_asistencia)">{{ m.estado_asistencia }}</span></td>
                  <td><span v-if="m.promedio_asistencia !== null" :title="`${m.presencias_periodo} de ${m.sesiones_posibles} sesiones`">{{ m.presencias_periodo }} de {{ m.sesiones_posibles }} ({{ m.promedio_asistencia }}%)</span><span v-else class="muted">—</span></td>
                  <td class="row-actions">
                    <b-button size="is-small" type="is-info is-light" icon-left="pencil" @click="editarMiembro(m, fam)">Ficha</b-button>
                    <b-button size="is-small" type="is-warning is-light" icon-left="account-remove" @click="quitarMiembro(m)">Quitar</b-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p v-else class="muted empty-members">Sin miembros asignados todavía.</p>
          </div>

          <div class="family-actions">
            <b-button size="is-small" type="is-primary is-light" icon-left="account-plus" @click="abrirAsignar(fam)">Asignar miembros</b-button>
            <b-button size="is-small" type="is-success is-light" icon-left="plus" @click="abrirNuevoMiembro(fam)">Nuevo miembro</b-button>
            <b-button size="is-small" type="is-info is-light" icon-left="pencil" @click="editarFamilia(fam)">Editar</b-button>
            <b-button size="is-small" :type="fam.activa ? 'is-warning is-light' : 'is-success is-light'" :icon-left="fam.activa ? 'pause' : 'play'" @click="alternarFamilia(fam)">{{ fam.activa ? 'Desactivar' : 'Activar' }}</b-button>
            <b-button size="is-small" type="is-danger is-light" icon-left="delete" @click="confirmarEliminarFamilia(fam)">Eliminar</b-button>
          </div>
        </div>

        <div v-if="sinFamiliaVisibles.length" class="unassigned-card">
          <div class="unassigned-head">
            <b-icon icon="account-question" size="is-small"></b-icon>
            <h3>Miembros sin familia ({{ sinFamiliaVisibles.length }})</h3>
          </div>
          <table class="table members-table">
            <thead>
              <tr>
                <th>Miembro</th>
                <th v-if="vistaGlobalSuper">Grupo</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in sinFamiliaVisibles" :key="m.id">
                <td><strong>{{ nombreCompleto(m) }}</strong></td>
                <td v-if="vistaGlobalSuper"><span class="tag" :style="estiloGrupo(m.grupoId)">{{ nombreGrupo(m.grupoId) }}</span></td>
                <td><span class="tag" :class="claseEstado(m.estado_asistencia)">{{ m.estado_asistencia }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal familia -->
      <b-modal :active.sync="modalFamilia" :width="640">
        <div class="box">
          <h3 class="title">{{ familiaForm.id ? 'Editar familia' : 'Nueva familia' }}</h3>
          <p class="modal-hint">{{ familiaForm.id ? 'Datos de la familia' : 'Busca al principal de la familia y se autollenarán sus datos' }}</p>

          <template v-if="!familiaForm.id">
            <b-field label="Principal de la familia (cabeza del hogar)">
              <b-autocomplete
                v-model="busquedaPrincipal"
                :data="miembrosFiltrados"
                placeholder="Escribe el nombre del miembro…"
                icon="magnify"
                open-on-focus
                clearable
                @select="seleccionarPrincipal">
                <template #default="{ option }">
                  {{ nombreCompleto(option) }}
                  <span class="tag is-info opt-tag">{{ nombreDelGrupo(option.grupoId) }}</span>
                </template>
              </b-autocomplete>
            </b-field>
            <div v-if="principalSel" class="principal-card">
              <b-icon icon="account-check" size="is-small"></b-icon>
              <span><strong>{{ nombreCompleto(principalSel) }}</strong> · {{ nombreDelGrupo(principalSel.grupoId) }}</span>
              <a class="quitar-principal" @click="quitarPrincipal">Quitar</a>
            </div>
          </template>

          <b-field grouped>
            <b-field label="Nombre del principal *" expanded><b-input v-model="familiaForm.nombre" required></b-input></b-field>
            <b-field label="Apellido" expanded><b-input v-model="familiaForm.apellido"></b-input></b-field>
          </b-field>
          <b-field label="Dirección"><b-input v-model="familiaForm.direccion"></b-input></b-field>
          <b-field grouped>
            <b-field label="Ciudad" expanded><b-input v-model="familiaForm.ciudad"></b-input></b-field>
            <b-field label="Iglesia / obra donde asisten" expanded><b-input v-model="familiaForm.iglesia"></b-input></b-field>
          </b-field>
          <b-field v-if="vistaGlobalSuper && !principalSel" label="Grupo">
            <b-select v-model="familiaForm.grupoId" placeholder="Seleccione grupo" expanded>
              <option v-for="g in gruposOrdenados" :value="g.id" :key="g.id">{{ g.nombre }}</option>
            </b-select>
          </b-field>
          <b-field v-else-if="vistaGlobalSuper && principalSel" label="Grupo">
            <b-input :value="nombreDelGrupo(familiaForm.grupoId)" disabled></b-input>
          </b-field>
          <b-field grouped>
            <b-button type="is-primary" @click="guardarFamilia" :disabled="!familiaForm.nombre || (vistaGlobalSuper && !familiaForm.id && !familiaForm.grupoId)">Guardar</b-button>
            <b-button @click="modalFamilia = false">Cancelar</b-button>
          </b-field>
        </div>
      </b-modal>

      <!-- Modal asignar miembros -->
      <b-modal :active.sync="modalAsignar" :width="560">
        <div class="box">
          <h3 class="title">Asignar miembros</h3>
          <p class="modal-hint">A <strong>{{ nombreFamilia(familiaSeleccionada) }}</strong> — todos los miembros sin censar, de cualquier grupo</p>
          <b-field label="Buscar miembro">
            <b-autocomplete
              v-model="busquedaAsignar"
              :data="candidatosFiltrados"
              placeholder="Escribe el nombre del miembro…"
              icon="magnify"
              open-on-focus
              clearable
              @select="agregarPorAsignar">
              <template #default="{ option }">
                {{ nombreCompleto(option) }}<span class="muted"> · {{ edadDe(option) }}</span>
                <span class="tag is-info opt-tag">{{ nombreDelGrupo(option.grupoId) }}</span>
              </template>
            </b-autocomplete>
          </b-field>
          <div v-if="porAsignar.length === 0" class="muted">Selecciona miembros en el buscador y marca su rol en la familia.</div>
          <div v-else class="assign-list">
            <div v-for="m in porAsignar" :key="m.id" class="assign-row">
              <span class="assign-nombre">{{ nombreCompleto(m) }}<span class="muted"> · {{ edadDe(m) }}</span> <span class="tag is-info">{{ nombreDelGrupo(m.grupoId) }}</span></span>
              <b-select v-model="m.rol_familiar" placeholder="Relación…" size="is-small" class="assign-rol">
                <option value="Esposa">Esposa</option>
                <option value="Hijo">Hijo</option>
                <option value="Hija">Hija</option>
                <option value="Otro">Otro</option>
              </b-select>
              <b-button size="is-small" type="is-danger is-light" icon-left="close" @click="quitarPorAsignar(m)"></b-button>
            </div>
          </div>
          <b-field grouped>
            <b-button type="is-primary" :disabled="porAsignar.length === 0 || porAsignar.some(m => !m.rol_familiar)" @click="guardarAsignacion">Asignar {{ porAsignar.length ? `(${porAsignar.length})` : '' }}</b-button>
            <b-button @click="modalAsignar = false">Cancelar</b-button>
          </b-field>
        </div>
      </b-modal>

      <!-- Modal ficha de miembro (censo) -->
      <b-modal :active.sync="modalMiembro" :width="720">
        <div class="box">
          <h3 class="title">{{ miembroForm.id ? 'Ficha del miembro' : 'Nuevo miembro' }}</h3>
          <form-miembro
            :miembro="miembroForm"
            :foto="fotoMiembro"
            :etiqueta-familia="familiaSeleccionada ? nombreFamilia(familiaSeleccionada) : null"
            :mostrar-notas="esSuper"
            :categoria-defecto="categoriaDefecto"
            :categoria-bloqueada="!esSuper"
            @guardar="guardarMiembro"
            @cancelar="modalMiembro = false"
          ></form-miembro>
        </div>
      </b-modal>
    </div>
  </section>
</template>

<script>
import HttpService from '../../Servicios/HttpService'
import AyudanteSesion from '../../Servicios/AyudanteSesion'
import FormMiembro, { formMiembroVacio } from './FormMiembro'

const FORM_FAMILIA_VACIO = () => ({ id: null, nombre: '', apellido: '', direccion: '', ciudad: '', estado: '', codigo_postal: '', iglesia: '', grupoId: null })
const FORM_MIEMBRO_VACIO = formMiembroVacio

export default {
  name: 'CensoComponent',
  components: { FormMiembro },
  data(){
    return {
      cargando: false,
      familias: [],
      sinFamilia: [],
      resumen: {},
      grupos: [],
      miGrupoId: null,
      miGrupoNombre: '',
      filtro: '',
      filtroEstado: 'todas',
      filtroGrupo: null,
      modalFamilia: false,
      modalAsignar: false,
      modalMiembro: false,
      familiaForm: FORM_FAMILIA_VACIO(),
      miembroForm: FORM_MIEMBRO_VACIO(),
      fotoMiembro: null,
      familiaSeleccionada: null,
      porAsignar: [],
      busquedaAsignar: '',
      miembrosTodos: [],
      sinFamiliaTodos: [],
      busquedaPrincipal: '',
      principalSel: null
    }
  },
  computed: {
    esSuper(){ return AyudanteSesion.esSuper() },
    vistaGlobalSuper(){ return this.esSuper && !AyudanteSesion.actuandoComoGrupo() },
    subtitulo(){
      return this.vistaGlobalSuper
        ? 'Todas las familias de la iglesia, por congregación'
        : 'Familias del grupo ' + (localStorage.getItem('grupoSeleccionadoNombre') || localStorage.getItem('grupoNombre') || 'activo')
    },
    gruposOrdenados(){
      return [...this.grupos].sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'))
    },
    cobertura(){
      const t = this.resumen.total_miembros || 0
      if(!t) return 0
      return Math.round(((this.resumen.miembros_en_familia || 0) * 100) / t)
    },
    familiasVisibles(){
      let lista = [...this.familias]
      if(this.filtroEstado === 'activas') lista = lista.filter(f => !!f.activa)
      if(this.filtroEstado === 'inactivas') lista = lista.filter(f => !f.activa)
      if(this.filtroGrupo) lista = lista.filter(f => String(f.grupoId) === String(this.filtroGrupo))
      if(this.filtro){
        const q = this.filtro.toLowerCase()
        lista = lista.filter(f =>
          (f.nombre || '').toLowerCase().includes(q) ||
          (f.apellido || '').toLowerCase().includes(q) ||
          (f.miembros || []).some(m => (m.nombre || '').toLowerCase().includes(q) || (m.apellido || '').toLowerCase().includes(q))
        )
      }
      return lista.sort((a, b) => this.nombreFamilia(a).localeCompare(this.nombreFamilia(b), 'es'))
    },
    sinFamiliaVisibles(){
      let lista = [...this.sinFamilia]
      if(this.filtroGrupo) lista = lista.filter(m => String(m.grupoId) === String(this.filtroGrupo))
      if(this.filtro){
        const q = this.filtro.toLowerCase()
        lista = lista.filter(m => (m.nombre || '').toLowerCase().includes(q) || (m.apellido || '').toLowerCase().includes(q))
      }
      return lista.sort((a, b) => String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es'))
    },
    // TODOS los miembros sin censar, de cualquier grupo (una familia puede mezclar categorías)
    candidatosAsignar(){
      if(!this.familiaSeleccionada) return []
      return this.sinFamiliaTodos
    },
    // buscador del modal asignar: sólo miembros sin familia (sin censar) del grupo de la familia,
    // excluyendo los que ya están en la lista de asignación
    candidatosFiltrados(){
      const q = (this.busquedaAsignar || '').toLowerCase()
      const enLista = this.porAsignar.map(m => m.id)
      return this.candidatosAsignar
        .filter(m => !enLista.includes(m.id))
        .filter(m => !q || this.nombreCompleto(m).toLowerCase().includes(q))
        .sort((a, b) => this.nombreCompleto(a).localeCompare(this.nombreCompleto(b), 'es'))
        .slice(0, 25)
    },
    miembrosFiltrados(){
      const q = (this.busquedaPrincipal || '').toLowerCase()
      return this.miembrosTodos
        .filter(m => !m.familiaId) // el principal también debe ser alguien aún sin censar
        .filter(m => !q || this.nombreCompleto(m).toLowerCase().includes(q))
        .sort((a, b) => this.nombreCompleto(a).localeCompare(this.nombreCompleto(b), 'es'))
        .slice(0, 25)
    },
    categoriaDefecto(){
      const gid = this.familiaSeleccionada ? this.familiaSeleccionada.grupoId : (localStorage.getItem('grupoSeleccionado') || localStorage.getItem('grupoId') || this.miGrupoId)
      return this.nombreDelGrupo(gid)
    }
  },
  mounted(){
    this.cargarTodo()
    if(this.esSuper){
      HttpService.obtenerConConsultas('grupos.php', { accion: 'obtener' })
        .then(r => { this.grupos = Array.isArray(r) ? r : [] })
    }
    window.addEventListener('grupoSeleccionadoChanged', this.cargarTodo)
    this.cargarMiGrupo()
    this.cargarMiembrosTodos()
  },
  beforeDestroy(){ window.removeEventListener('grupoSeleccionadoChanged', this.cargarTodo) },
  methods: {
    nombreDelGrupo(gid){
      if(!gid) return ''
      const g = this.grupos.find(x => String(x.id) === String(gid))
      if(g) return g.nombre
      if(String(gid) === String(this.miGrupoId) && this.miGrupoNombre) return this.miGrupoNombre
      if(String(gid) === String(localStorage.getItem('grupoSeleccionado'))) return localStorage.getItem('grupoSeleccionadoNombre') || ''
      if(String(gid) === String(localStorage.getItem('grupoId'))) return localStorage.getItem('grupoNombre') || ''
      return ''
    },
    cargarMiGrupo(){
      if(this.esSuper) return
      const guardado = localStorage.getItem('grupoId')
      const nombre = localStorage.getItem('grupoNombre')
      if(guardado && nombre){ this.miGrupoId = Number(guardado); this.miGrupoNombre = nombre; return }
      const sesId = localStorage.getItem('id')
      if(!sesId) return
      HttpService.obtenerConConsultas('usuarios.php', { accion: 'obtener_por_id', id: Number(sesId) })
      .then(u => {
        if(!u || !u.grupoId) return
        this.miGrupoId = u.grupoId
        HttpService.obtenerConConsultas('grupos.php', { accion: 'obtener_por_id', id: u.grupoId })
        .then(g => { if(g && g.nombre) this.miGrupoNombre = g.nombre })
      })
    },
    nombreFamilia(f){
      if(!f) return ''
      return [f.apellido, f.nombre].filter(Boolean).join(', ') || 'Familia'
    },
    nombreCompleto(m){
      return [m.nombre, m.apellido].filter(Boolean).join(' ')
    },
    // el principal se muestra como "Esposo" y siempre primero; luego Esposa, Hijo, Hija
    etiquetaRol(rol){
      return rol === 'Principal' ? 'Esposo' : rol
    },
    claseRol(rol){
      const r = rol === 'Principal' ? 'Esposo' : rol
      return { Esposo: 'rol-esposo', Esposa: 'rol-esposa' }[r] || ''
    },
    miembrosOrdenados(fam){
      const orden = { Principal: 0, Esposo: 0, Esposa: 1, Hijo: 2, Hija: 3, Otro: 4 }
      return [...(fam.miembros || [])].sort((a, b) => {
        const oa = orden[a.rol_familiar] !== undefined ? orden[a.rol_familiar] : 5
        const ob = orden[b.rol_familiar] !== undefined ? orden[b.rol_familiar] : 5
        if(oa !== ob) return oa - ob
        return this.nombreCompleto(a).localeCompare(this.nombreCompleto(b), 'es')
      })
    },
    edadDe(m){
      if(m.fecha_nacimiento){
        const nac = new Date(m.fecha_nacimiento + 'T00:00:00')
        if(!isNaN(nac)){
          const hoy = new Date()
          let e = hoy.getFullYear() - nac.getFullYear()
          const mo = hoy.getMonth() - nac.getMonth()
          if(mo < 0 || (mo === 0 && hoy.getDate() < nac.getDate())) e--
          if(e >= 0) return e + ' años'
        }
      }
      return (m.edad !== null && m.edad !== undefined && m.edad !== '') ? m.edad + ' años' : '—'
    },
    claseEstado(estado){
      if(estado === 'Activo') return 'is-success'
      if(estado === 'Retirado temporal') return 'is-warning'
      return 'is-danger'
    },
    nombreGrupo(id){
      const g = this.grupos.find(x => String(x.id) === String(id))
      return g ? g.nombre : 'Grupo ' + id
    },
    estiloGrupo(id){
      const palette = ['#7C3AED','#06B6D4','#F59E0B','#EF4444','#10B981','#8B5CF6','#F43F5E']
      const color = palette[(Number(id) || 0) % palette.length]
      return { background: color + '1f', color: color }
    },
    cargarTodo(){
      this.cargando = true
      this.cargarMiembrosTodos()
      HttpService.obtenerConConsultas('familias.php', { accion: 'obtener_completo' })
        .then(r => {
          if(r && r.error){ this.$buefy.toast.open({ type: 'is-danger', message: r.error }); return }
          this.familias = (r && r.familias) || []
          this.sinFamilia = (r && r.sin_familia) || []
          this.resumen = (r && r.resumen) || {}
        })
        .finally(() => { this.cargando = false })
    },

    // Todos los miembros visibles para elegir principal (el backend ya los filtra por grupo)
    cargarMiembrosTodos(){
      HttpService.obtenerConConsultas('miembros.php', { accion: 'obtener' })
      .then(r => { this.miembrosTodos = Array.isArray(r) ? r : [] })
      // sin censar de TODOS los grupos, para el buscador de asignación
      HttpService.obtenerConConsultas('familias.php', { accion: 'obtener_sin_familia' })
      .then(r => { this.sinFamiliaTodos = Array.isArray(r) ? r : [] })
    },
    seleccionarPrincipal(m){
      if(!m) return
      this.principalSel = m
      this.busquedaPrincipal = ''
      this.familiaForm.nombre = m.nombre || ''
      this.familiaForm.apellido = m.apellido || ''
      if(m.direccion) this.familiaForm.direccion = m.direccion
      if(m.ciudad) this.familiaForm.ciudad = m.ciudad
      this.familiaForm.grupoId = m.grupoId
    },
    quitarPrincipal(){
      this.principalSel = null
      this.familiaForm.nombre = ''
      this.familiaForm.apellido = ''
      this.familiaForm.direccion = ''
      this.familiaForm.ciudad = ''
      this.familiaForm.grupoId = null
    },

    // ---------- Familias ----------
    abrirNuevaFamilia(){
      this.familiaForm = { ...FORM_FAMILIA_VACIO(), iglesia: 'HERMOSA PROVINCIA' }
      this.principalSel = null
      this.busquedaPrincipal = ''
      if(!this.vistaGlobalSuper){
        const g = localStorage.getItem('grupoSeleccionado') || localStorage.getItem('grupoId') || this.miGrupoId
        this.familiaForm.grupoId = g ? Number(g) : null
      }
      this.modalFamilia = true
    },
    editarFamilia(fam){
      this.familiaForm = { ...FORM_FAMILIA_VACIO(), ...fam }
      this.principalSel = null
      this.busquedaPrincipal = ''
      this.modalFamilia = true
    },
    guardarFamilia(){
      const accion = this.familiaForm.id ? 'editar' : 'registrar'
      const payload = { accion, familia: { ...this.familiaForm } }
      if(this.principalSel) payload.miembroId = this.principalSel.id
      if(this.vistaGlobalSuper && payload.familia.grupoId) payload.grupoId = payload.familia.grupoId
      HttpService.registrar('familias.php', payload).then(r => {
        if(r === false || (r && r.error)){
          this.$buefy.toast.open({ type: 'is-danger', message: 'No se pudo guardar la familia' })
          return
        }
        this.$buefy.toast.open({ type: 'is-success', message: 'Familia guardada' })
        this.modalFamilia = false
        this.cargarTodo()
      })
    },
    alternarFamilia(fam){
      HttpService.registrar('familias.php', { accion: 'cambiar_estado', id: fam.id, activa: fam.activa ? 0 : 1 })
        .then(r => {
          if(r === false){ this.$buefy.toast.open({ type: 'is-danger', message: 'No se pudo cambiar el estado' }); return }
          this.cargarTodo()
        })
    },
    confirmarEliminarFamilia(fam){
      this.$buefy.dialog.confirm({
        title: 'Eliminar familia',
        message: `¿Eliminar <strong>${this.nombreFamilia(fam)}</strong>? Los miembros quedarán sin familia, no se borran.`,
        confirmText: 'Eliminar',
        type: 'is-danger',
        onConfirm: () => {
          HttpService.eliminar('familias.php', { accion: 'eliminar', id: fam.id }).then(() => this.cargarTodo())
        }
      })
    },

    // ---------- Asignación ----------
    abrirAsignar(fam){
      this.familiaSeleccionada = fam
      this.porAsignar = []
      this.busquedaAsignar = ''
      this.modalAsignar = true
    },
    agregarPorAsignar(m){
      if(!m) return
      this.porAsignar.push({ ...m, rol_familiar: null })
      this.busquedaAsignar = ''
    },
    quitarPorAsignar(m){
      this.porAsignar = this.porAsignar.filter(x => x.id !== m.id)
    },
    guardarAsignacion(){
      HttpService.registrar('familias.php', {
        accion: 'asignar_miembros',
        familiaId: this.familiaSeleccionada.id,
        miembros: this.porAsignar.map(m => ({ id: m.id, rol_familiar: m.rol_familiar }))
      }).then(r => {
        if(r === false){ this.$buefy.toast.open({ type: 'is-danger', message: 'No se pudo asignar' }); return }
        this.modalAsignar = false
        this.$buefy.toast.open({ type: 'is-success', message: 'Miembros asignados' })
        this.cargarTodo()
      })
    },
    quitarMiembro(m){
      this.$buefy.dialog.confirm({
        title: 'Quitar de la familia',
        message: `¿Quitar a <strong>${this.nombreCompleto(m)}</strong> de esta familia? Quedará como "sin familia".`,
        confirmText: 'Quitar',
        type: 'is-warning',
        onConfirm: () => {
          HttpService.registrar('familias.php', { accion: 'quitar_miembro', miembroId: m.id }).then(() => this.cargarTodo())
        }
      })
    },

    // ---------- Ficha de miembro ----------
    abrirNuevoMiembro(fam){
      this.familiaSeleccionada = fam
      this.fotoMiembro = null
      this.miembroForm = { ...FORM_MIEMBRO_VACIO(), familiaId: fam.id, grupoId: fam.grupoId }
      this.modalMiembro = true
    },
    editarMiembro(m, fam){
      this.familiaSeleccionada = fam
      this.fotoMiembro = null
      this.miembroForm = { ...FORM_MIEMBRO_VACIO(), ...m, es_espiritual: Number(m.es_espiritual) ? 1 : 0 }
      this.modalMiembro = true
      HttpService.obtenerConConsultas('familias.php', { accion: 'obtener_foto', miembroId: m.id })
        .then(r => { if(r && r.foto) this.fotoMiembro = r.foto })
    },
    guardarMiembro(datos){
      const cliente = { ...datos }
      delete cliente.foto; delete cliente.fotoNueva
      if(!this.esSuper) delete cliente.notas
      const accion = datos.id ? 'editar' : 'registrar'
      const payload = { accion, cliente }
      if(this.vistaGlobalSuper || AyudanteSesion.actuandoComoGrupo()) payload.grupoId = datos.grupoId
      HttpService.registrar('miembros.php', payload).then(r => {
        if(r === false || (r && r.error)){
          this.$buefy.toast.open({ type: 'is-danger', message: 'No se pudo guardar el miembro' })
          return
        }
        const miembroId = datos.id || (typeof r === 'number' ? r : null)
        const terminar = () => { this.modalMiembro = false; this.$buefy.toast.open({ type: 'is-success', message: 'Miembro guardado' }); this.cargarTodo() }
        if(datos.fotoNueva && datos.foto && miembroId){
          HttpService.registrar('familias.php', { accion: 'guardar_foto', miembroId, foto: datos.foto }).then(terminar)
        } else terminar()
      })
    }
  }
}
</script>

<style scoped>
.container{ padding: 0; }
.muted{ color: var(--text-muted); font-weight: 400; }
.censo-loading{ text-align: center; color: var(--text-muted); padding: 3rem 0; }
.modal-hint{ color: var(--text-muted); font-size: .82rem; margin-top: -.5rem; margin-bottom: 1rem; }
.section-label{
  font-size: .68rem; font-weight: 700; letter-spacing: .08em; text-transform: uppercase;
  color: #9aa0b8; margin: 1.1rem 0 .6rem; border-top: 1px solid var(--border); padding-top: 1rem;
}

.empty-card{
  background: #fff; border: 1px dashed #d8dcee; border-radius: var(--radius-card);
  padding: 3rem 1.5rem; text-align: center; color: var(--text-muted);
  display: flex; flex-direction: column; align-items: center; gap: .6rem;
}
.empty-card .icon{ color: #c6cbe0; }

.family-card{
  background: #fff; border: 1px solid var(--border); border-radius: var(--radius-card);
  box-shadow: var(--shadow-card); padding: 1.1rem 1.25rem; margin-bottom: 1rem;
}
.family-card.is-inactive{ opacity: .72; }
.family-head{ display: flex; justify-content: space-between; align-items: flex-start; gap: .75rem; flex-wrap: wrap; }
.family-title{ display: flex; align-items: center; gap: .7rem; }
.family-icon{
  width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0;
  background: var(--brand-soft); color: var(--brand-dark);
  display: flex; align-items: center; justify-content: center;
}
.family-title h3{ font-size: 1.02rem; font-weight: 700; color: var(--text); margin: 0; }
.family-meta{ font-size: .76rem; color: var(--text-muted); margin: .1rem 0 0; }
.family-badges{ display: flex; gap: .4rem; flex-wrap: wrap; }
.family-address{
  display: flex; align-items: center; gap: .4rem; margin-top: .55rem;
  font-size: .82rem; color: #555d78;
}
.family-address .icon{ color: var(--text-muted); }

.members-table{ margin: .6rem 0 0; font-size: .86rem; width: 100%; }
.members-table th{ padding: .45rem .6rem; }
.members-table td{ padding: .55rem .6rem; }
.row-actions{ text-align: right; white-space: nowrap; }
.row-actions .button + .button{ margin-left: .3rem; }
.empty-members{ margin: .7rem 0 0; font-size: .85rem; }

.family-actions{
  display: flex; gap: .4rem; flex-wrap: wrap;
  margin-top: .9rem; padding-top: .8rem; border-top: 1px solid #f1f3f9;
}

.unassigned-card{
  background: #fffdf7; border: 1px solid #fbe3bb; border-radius: var(--radius-card);
  padding: 1.1rem 1.25rem; margin-top: 1.25rem;
}
.unassigned-head{ display: flex; align-items: center; gap: .5rem; margin-bottom: .4rem; }
.unassigned-head .icon{ color: var(--warning); }
.unassigned-head h3{ font-size: .95rem; font-weight: 700; color: #92600d; margin: 0; }

.assign-list{ display: flex; flex-direction: column; gap: .5rem; max-height: 320px; overflow-y: auto; margin-bottom: 1rem; }
.assign-row{ display: flex; align-items: center; gap: .6rem; background: #f8f7ff; border: 1px solid #eceafe; border-radius: 10px; padding: .45rem .7rem; }
.assign-nombre{ flex: 1; font-size: .9rem; }
.assign-rol{ width: 130px; }
.rol-tag{ background: #f0eaff; color: var(--brand-dark); margin-right: .35rem; }
.rol-tag.rol-esposo{ background: #dcf5e8; color: #15803d; }
.rol-tag.rol-esposa{ background: #fde4ee; color: #be185d; }

.principal-card{
  display: flex; align-items: center; gap: .5rem;
  background: var(--brand-soft); border: 1px solid #ddd6fe;
  border-radius: 10px; padding: .55rem .8rem; margin-bottom: .8rem;
  font-size: .86rem; color: var(--brand-dark);
}
.principal-card .icon{ color: var(--brand); }
.quitar-principal{ margin-left: auto; font-size: .78rem; font-weight: 600; color: var(--danger); }
.opt-tag{ margin-left: .4rem; }
.hint-warn{ font-size: .78rem; color: #b45309; margin: -.4rem 0 .8rem; }
</style>
